/**
 * Search API Client — OpenSearch V2 搜索接口
 */

import { apiClient } from './client';

export interface SearchRequest {
  query?: string;
  city?: string;
  category?: string;
  minRating?: number;
  priceLevel?: number;
  lat?: number;
  lng?: number;
  radiusKm?: number;
  page?: number;
  size?: number;
}

export interface SearchResult<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  tookMs: number;
  source: string;
  hasNext: boolean;
}

export interface PoiSearchDoc {
  id: number;
  name: string;
  address: string;
  category: string;
  subCategory: string;
  tags: string[];
  rating: number;
  latitude: number;
  longitude: number;
  priceLevel: number;
  city: string;
  description: string;
  popularity: number;
  imageUrl: string;
}

export interface HotelSearchDoc {
  id: number;
  name: string;
  address: string;
  brand: string;
  rating: number;
  starLevel: number;
  latitude: number;
  longitude: number;
  priceLevel: number;
  city: string;
  description: string;
  amenities: string[];
  reviewScore: number;
  reviewCount: number;
  imageUrl: string;
}

export interface GuideDoc {
  id: number;
  title: string;
  content: string;
  city: string;
  tags: string[];
  author: string;
  viewCount: number;
  rating: number;
  coverUrl: string;
}

/**
 * 搜索 POI
 */
export async function searchPois(params: SearchRequest): Promise<SearchResult<PoiSearchDoc>> {
  const response = await apiClient.get<SearchResult<PoiSearchDoc>>('/search/v2/pois', params as Record<string, unknown>);
  return response.data ?? { items: [], total: 0, page: 0, size: 20, tookMs: 0, source: 'none', hasNext: false };
}

/**
 * 搜索酒店
 */
export async function searchHotels(params: SearchRequest): Promise<SearchResult<HotelSearchDoc>> {
  const response = await apiClient.get<SearchResult<HotelSearchDoc>>('/search/v2/hotels', params as Record<string, unknown>);
  return response.data ?? { items: [], total: 0, page: 0, size: 20, tookMs: 0, source: 'none', hasNext: false };
}

/**
 * 搜索景点
 */
export async function searchAttractions(params: SearchRequest): Promise<SearchResult<PoiSearchDoc>> {
  const response = await apiClient.get<SearchResult<PoiSearchDoc>>('/search/v2/attractions', params as Record<string, unknown>);
  return response.data ?? { items: [], total: 0, page: 0, size: 20, tookMs: 0, source: 'none', hasNext: false };
}

/**
 * 搜索攻略
 */
export async function searchGuides(params: SearchRequest): Promise<SearchResult<GuideDoc>> {
  const response = await apiClient.get<SearchResult<GuideDoc>>('/search/v2/guides', params as Record<string, unknown>);
  return response.data ?? { items: [], total: 0, page: 0, size: 20, tookMs: 0, source: 'none', hasNext: false };
}

/**
 * 搜索建议（自动补全）
 */
export async function searchSuggest(query: string): Promise<string[]> {
  const response = await apiClient.get<string[]>('/search/v2/suggest', { query });
  return response.data ?? [];
}
